// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Login from './pages/Login/Login';
import Layout from './components/layout/Layout';
import UserManagement from './pages/UserManagement/UserManagement';
import PaymentManagement from './pages/PaymentManagement/PaymentManagement';
import SystemAnalytics from './pages/SystemAnalytics/SystemAnalytics';
import AIMonitoring from './pages/AIMonitoring/AIMonitoring';
import MotivationSystem from './pages/MotivationSystem/MotivationSystem';
import ReportingModule from './pages/ReportingModule/ReportingModule';
import SystemHealthDashboard from './pages/SystemHealthDashboard/SystemHealthDashboard';
import OrganizationManagement from './pages/OrganizationManagement/OrganizationManagement';
import PlanManagement from './pages/PlanManagement/PlanManagement';

import './styles/globals.scss';
import NotificationManagement from './pages/NotificationManagement/NotificationManagement';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        Yükleniyor...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route component
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        Yükleniyor...
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Ana uygulama içeriği
const AppContent = () => {
  const { isDark } = useTheme();
  const [activeSection, setActiveSection] = useState('user-list'); // Varsayılan user-list

  useEffect(() => {
    if (isDark) {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
  }, [isDark]);

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'user-list':
        return <UserManagement />;
      case 'payment-list':
      case 'revenue-stats':
        return <PaymentManagement activeView={activeSection} />;
      case 'general-stats':
      case 'usage-analytics':
        return <SystemAnalytics />;
      case 'ai-usage':
      case 'ai-costs':
        return <AIMonitoring />;
      case 'motivational-messages':
      case 'motivation-stats':
        return <MotivationSystem />;
      case 'send-notification':
      case 'notification-history':
        return <NotificationManagement activeView={activeSection} />;
      case 'custom-reports':
        return <ReportingModule />;
      case 'system-health':
        return <SystemHealthDashboard />;
      case 'org-list':
      case 'org-create':
        return <OrganizationManagement activeView={activeSection} />;
      case 'plan-list':
        return <PlanManagement />;

      // Diğer section'lar buraya eklenecek
      default:
        return <UserManagement />;
    }
  };

  return (
    <Layout
      activeSection={activeSection}
      onSectionChange={setActiveSection}
    >
      {renderActiveSection()}
    </Layout>
  );
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <LanguageProvider>
          <Router>
            <div className="App">
              <Routes>
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <AppContent />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </Router>
        </LanguageProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;